import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiBaseUrl } from '../../api/client'
import type { components } from '../../api/schema'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

type PublicViewing = components['schemas']['PublicViewing']

function base(orgCode: string, token: string, viewingUid: string) {
    return `${apiBaseUrl}/c/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/viewings/${encodeURIComponent(viewingUid)}`
}

/**
 * The applicant's confirm page (spec 2026-10-25-12): the viewing and one
 * button. The reconfirmation e-mail links here; the thread token is the whole
 * authorization.
 */
export function PublicViewingConfirm({ orgCode, token, viewingUid }: { orgCode: string; token: string; viewingUid: string }) {
    const { t, i18n } = useTranslation('viewings')
    const queryClient = useQueryClient()
    const key = ['public-viewing', orgCode, token, viewingUid]
    const [error, setError] = useState<string | null>(null)
    const { data, isLoading } = useQuery({
        queryKey: key,
        retry: false,
        queryFn: async (): Promise<PublicViewing | null> => {
            const res = await fetch(base(orgCode, token, viewingUid) + '/state')
            if (res.status === 404) return null
            if (!res.ok) throw new Error(String(res.status))
            return ((await res.json()) as { viewing: PublicViewing }).viewing
        },
    })
    const confirm = async () => {
        setError(null)
        const res = await fetch(base(orgCode, token, viewingUid) + '/confirm', { method: 'POST' })
        if (!res.ok) {
            setError(t('public.error'))
            return
        }
        queryClient.setQueryData(key, ((await res.json()) as { viewing: PublicViewing }).viewing)
    }
    const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'full', timeStyle: 'short' })

    return (
        <div className="mx-auto max-w-lg p-4" data-testid="public-viewing-page">
            <Card>
                <CardHeader>
                    <CardTitle>{t('public.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    {isLoading && <p className="text-muted-foreground">{t('public.loading')}</p>}
                    {!isLoading && !data && <p data-testid="public-viewing-not-found">{t('public.not_found')}</p>}
                    {data && (
                        <>
                            <p className="font-medium" data-testid="public-viewing-asset">{data.asset_name}</p>
                            <p data-testid="public-viewing-when">{when(data.starts_at)}</p>
                            <p className="text-muted-foreground">{t(`kind.${data.kind}`, { defaultValue: data.kind })}</p>
                            {data.status === 'confirmed' ? (
                                <p className="text-success" data-testid="public-viewing-confirmed">{t('public.confirmed')}</p>
                            ) : data.confirmable ? (
                                <Button onClick={() => void confirm()} data-testid="public-viewing-confirm">{t('public.confirm')}</Button>
                            ) : (
                                <p className="text-muted-foreground" data-testid="public-viewing-closed">{t('public.closed')}</p>
                            )}
                            {error && <p className="text-destructive" data-testid="public-viewing-error">{error}</p>}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
